import { FC } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  createFilterOptions,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";

import { DashboardLocIdInfo } from "../../../../../../redux/actions/dashboard/dashboardTypes";

import { DataSBFilterValues } from "../../../../../../redux/actions/dataTable/DataTableTypes";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  QualityCriteriaData,
  AddingCriteria,
  AdvancedDeclarationOption,
} from "../../../../../../redux/slices/grainTracker/grainTrackerTypes";
import { formRenderQualityCriteriaStyles } from "./qualityCriteriaPartsStyles/FormRenderQualityCriteriaInputsToCreateStyles";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

type RenderQualityCriteriaInputsToCreateParams = {
  selectedCriteriaTemplate: QualityCriteriaData[];
  t: (arg0: string) => string;
  addingCriteria: AddingCriteria;
  setCriteriaToBin: (addingCriteria: AddingCriteria) => void;
  resetAddingCriteria: () => void;
  setAdvancedDeclarationOptionText: (
    advancedDeclarationOption: AdvancedDeclarationOption,
    searchText: string,
  ) => void;
  advancedDeclarationOption: AdvancedDeclarationOption;
  setAvancedDeclarationOptionSelected: (values: {
    allValuesSelected: boolean;
    valuesSelected: DataSBFilterValues[];
  }) => void;
  advancedDeclarationOptionSelected: {
    allValuesSelected: boolean;
    valuesSelected: DataSBFilterValues[];
  };
  selectedCriteria: string | undefined;
};

const RenderQualityCriteriaInputsToCreate: FC<
  RenderQualityCriteriaInputsToCreateParams
> = ({
  selectedCriteriaTemplate,
  selectedCriteria,
  t,
  addingCriteria,
  setCriteriaToBin,
  resetAddingCriteria,
  setAdvancedDeclarationOptionText,
  advancedDeclarationOption,
  setAvancedDeclarationOptionSelected,
  advancedDeclarationOptionSelected,
}) => {
  if (
    selectedCriteriaTemplate[0]?.allotmentOperators?.every((x) => x !== "in")
  ) {
    return (
      <div className="container-criteria-values">
        <div className="should-be-GT-criteria">
          {t("Grain_tracker.should-be")}
        </div>
        <FormControl sx={formRenderQualityCriteriaStyles.FormControlContainer}>
          <Select
            required
            onChange={(e) => {
              setCriteriaToBin({
                ...addingCriteria,
                values: {
                  ...addingCriteria.values,
                  allotmentOperator: e.target.value as string,
                  storageQualityCriterionId: selectedCriteriaTemplate[0].id,
                  allotmentValueArray: null,
                },
              });
            }}
          >
            {selectedCriteriaTemplate[0]?.allotmentOperators?.map((values) => {
              return (
                <MenuItem key={values} value={values}>
                  {values === "<"
                    ? t("Grain_tracker.strict-below")
                    : t("Grain_tracker.strict-above")}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <TextField
          label={""}
          sx={formRenderQualityCriteriaStyles.FormControlContainerTextField}
          type="number"
          InputLabelProps={{ shrink: true }}
          value={
            parseFloat(addingCriteria.values.allotmentValue).toString() ?? 0
          }
          onChange={(e) => {
            const handleNumber = () => {
              if (
                selectedCriteriaTemplate[0]?.qualityCriterionUnit.symbolTextId
                  .text === "%"
              ) {
                return parseFloat(e.target.value) >= 100
                  ? "100"
                  : parseFloat(e.target.value).toFixed(2);
              } else {
                return parseFloat(e.target.value).toFixed(2);
              }
            };
            setCriteriaToBin({
              ...addingCriteria,
              values: {
                ...addingCriteria.values,
                allotmentValue: handleNumber().toString(),
              },
            });
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {
                  selectedCriteriaTemplate[0]?.qualityCriterionUnit.symbolTextId
                    .text
                }
              </InputAdornment>
            ),
            inputProps: { step: "any", min: 0 },
          }}
        />
        <IconButton
          onClick={() => {
            resetAddingCriteria();
          }}
          size="small"
        >
          <DeleteIcon fontSize="inherit" />
        </IconButton>
      </div>
    );
  } else if (
    selectedCriteriaTemplate[0]?.allotmentOperators?.some((x) => x === "in")
  ) {
    const tagsOptions = advancedDeclarationOption?.tagsResults?.map(
      (values) => ({
        name: values.customerFacingTextId
          ? values.customerFacingTextId.text
          : values.defaultCustomerFacingText,
        id: values.id,
      }),
    );
    const transform = tagsOptions?.map((c: any) => c);

    return (
      <>
        <Autocomplete
          multiple
          disableListWrap={true}
          options={tagsOptions as any}
          disableCloseOnSelect
          filterOptions={(options, params) => {
            const filter = createFilterOptions();
            const filtered = filter(options, params as any);
            return [
              { name: t("Dashboard.setAllLocations"), id: 0 },
              ...filtered,
            ] as any;
          }}
          value={advancedDeclarationOptionSelected.valuesSelected as any}
          getOptionLabel={(option: DashboardLocIdInfo) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_e, value, reason) => {
            if (reason === "clear" || reason === "removeOption")
              setAvancedDeclarationOptionSelected({
                allValuesSelected: false,
                valuesSelected: [],
              });

            if (
              reason === "selectOption" &&
              value.length === tagsOptions.length
            )
              setAvancedDeclarationOptionSelected({
                allValuesSelected: true,
                valuesSelected: transform as any,
              });

            setCriteriaToBin({
              ...addingCriteria,
              values: {
                ...addingCriteria.values,
                storageQualityCriterionId: selectedCriteriaTemplate[0].id,
                allotmentOperator: selectedCriteriaTemplate.filter(
                  (v) => v.id === selectedCriteria,
                )[0].allotmentOperators[0],
              },
            });

            setAvancedDeclarationOptionSelected({
              allValuesSelected: false,
              valuesSelected: value,
            });

            if (reason === "selectOption" && tagsOptions.length === 0)
              console.log("");
          }}
          renderInput={(params: { InputLabelProps: any }) => {
            return (
              <TextField
                {...params}
                label={t("Common_options.type_to_search")}
                variant="outlined"
                placeholder={t("Common_options.search_variety_placeholder")}
                size="medium"
                onChange={(e) => {
                  setAdvancedDeclarationOptionText(
                    advancedDeclarationOption,
                    e.target.value,
                  );
                }}
                sx={formRenderQualityCriteriaStyles.Autocomplete}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            );
          }}
          renderTags={(list) => {
            const handleDelete = (idToRemove: any) => {
              const updated =
                advancedDeclarationOptionSelected.valuesSelected.filter(
                  (v) => v.id !== idToRemove,
                );
              setAvancedDeclarationOptionSelected({
                allValuesSelected: false,
                valuesSelected: updated,
              });
            };

            if (list.length === 1) {
              return (
                <Box sx={formRenderQualityCriteriaStyles.AutocompleteTags}>
                  {list.map((value: any) => (
                    <Chip
                      key={value.id}
                      label={value.name}
                      onDelete={() => handleDelete(value.id)}
                    />
                  ))}
                </Box>
              );
            }

            if (list.length > 1) {
              const tooltipTitle = (
                <ul style={{ margin: 0, paddingLeft: "1rem" }}>
                  {list.map((v: any) => (
                    <li
                      key={v.id}
                      style={formRenderQualityCriteriaStyles.tooltipListItem}
                    >
                      <span>{v.name}</span>
                      <span
                        style={{ cursor: "pointer", fontWeight: "bold" }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDelete(v.id);
                        }}
                      >
                        ✕
                      </span>
                    </li>
                  ))}
                </ul>
              );
              return (
                <Box sx={formRenderQualityCriteriaStyles.AutocompleteTags}>
                  <Tooltip
                    title={tooltipTitle}
                    arrow
                    disableFocusListener
                    componentsProps={{
                      tooltip: { sx: { maxWidth: 320, fontSize: "0.85rem" } },
                    }}
                  >
                    <Chip
                      label={`${list[0].name} + ${list.length - 1} ${t(
                        "Common_options.others",
                      )}`}
                    />
                  </Tooltip>
                </Box>
              );
            }
          }}
          renderOption={(props, option, { selected }) => {
            if (option.id === 0) {
              return (
                <Box
                  key={option.id}
                  onMouseDown={(e) => e.preventDefault()}
                  pl={1.5}
                  py={0.5}
                  sx={formRenderQualityCriteriaStyles.stickyOptionBox}
                >
                  <FormControlLabel
                    onClick={(e) => {
                      if (tagsOptions.length >= 1) {
                        e.preventDefault();
                        !advancedDeclarationOptionSelected.allValuesSelected
                          ? setAvancedDeclarationOptionSelected({
                              allValuesSelected: true,
                              valuesSelected: transform,
                            })
                          : setAvancedDeclarationOptionSelected({
                              allValuesSelected: false,
                              valuesSelected: [],
                            });
                      }
                    }}
                    disabled={tagsOptions.length === 0}
                    label={t("Dashboard.setAllLocations")}
                    control={
                      <Checkbox
                        disabled={tagsOptions.length === 0}
                        checked={
                          advancedDeclarationOptionSelected.allValuesSelected
                        }
                      />
                    }
                  />
                </Box>
              );
            }

            return (
              <li {...props}>
                <Checkbox
                  key={option.id}
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={formRenderQualityCriteriaStyles.checkboxOption}
                  checked={
                    option.id === 0
                      ? !!(
                          advancedDeclarationOptionSelected.valuesSelected.filter(
                            (x) => x.id !== undefined,
                          ).length === tagsOptions.length
                        )
                      : selected
                  }
                />

                <div>{`${option.name}`}</div>
              </li>
            );
          }}
          renderGroup={(params) => params as unknown as React.ReactNode}
        />

        <IconButton
          onClick={() => {
            resetAddingCriteria();
          }}
          size="small"
        >
          <DeleteIcon fontSize="inherit" />
        </IconButton>
      </>
    );
  }
  return (
    <div>
      <div></div>
    </div>
  );
};

export default RenderQualityCriteriaInputsToCreate;
